import React, { Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { noop } from 'lodash';
import styled from 'styled-components';
import { grey10, grey50 } from '@zocdoc/branding/colors';
import { CustomMetrics, Metrics, MetricsClickable, EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';
import { FontFamily } from '../../../../typography';
import { PhotoCapture } from '../../../../PhotoCapture/index';
import { Checkbox } from '../../../../Checkbox/index';
import { Link } from '../../../../link/index';
import { Modal, ModalHeader, ModalBodyCopy, ModalButtonContainer } from '../../../../modal';
import { Button } from '../../../../button';
import { LOCALIZED_STRINGS } from '../../../constants';

class BaseScanCardUploadView extends Component {
    static defaultProps = {
        closeScanCard: noop,
        fetchScanCard: noop,
        privacyCheckboxClicked: noop,
        dismissPrivacyDialog: noop,
    };

    componentDidMount() {
        // Closes the branch banner/journey from the homepage
        if (window.branch && window.branch.closeJourney) {
            window.branch.closeJourney();
        }
    }

    renderModal() {
        return (
            <CustomMetrics>
                {({ sendMetric }) => {
                    const handleCancel = () => {
                        this.props.dismissPrivacyDialog();
                        sendMetric({
                            action: 'Insurance Picker Scan - Privacy Dialog Dismissed',
                            element: 'DismissPrivacyDialog',
                            initiator: EVENT_INITIATORS.USER,
                            interactionType: INTERACTION_TYPES.CLICK,
                        });
                    };

                    return (
                        <Modal
                            data-test="modal-component"
                            onClickCancel={handleCancel}
                        >
                            <ModalHeader>
                                <FormattedMessage
                                    defaultMessage="We can't scan your card"
                                    id="Components.InsurancePicker.ScanCardUpload.CantScanModalHeader"
                                />
                            </ModalHeader>
                            <ModalBodyCopy>
                                <FormattedMessage
                                    defaultMessage="Sorry, we can't scan your card if you don't accept the Terms of Use and Privacy Policy"
                                    id="Components.InsurancePicker.ScanCardUpload.CantScanModalBody"
                                />
                            </ModalBodyCopy>
                            <ModalButtonContainer>
                                <Button
                                    fluid
                                    onClick={handleCancel}
                                >
                                    <FormattedMessage
                                        defaultMessage="OK, got it"
                                        id="Components.InsurancePicker.ScanCardUpload.CantScanModalBtnText"
                                    />
                                </Button>
                            </ModalButtonContainer>
                        </Modal>
                    );
                }}
            </CustomMetrics>
        );
    }

    renderFooter() {
        return (
            <ReturnToPickerLinkContainer>
                <MetricsClickable
                    action="Insurance Picker Scan - Close Scan Card View"
                    element="SkipAndChooseFromList"
                >
                    <ReturnToPickerLink
                        data-test="skip-and-choose-from-list"
                        onClick={this.closeScanCardFromFooter}
                    >
                        <FormattedMessage
                            defaultMessage="Skip & choose from insurance list"
                            id="Components.InsurancePicker.ScanCardUpload.ManuallySelectInsurance"
                        />
                    </ReturnToPickerLink>
                </MetricsClickable>
            </ReturnToPickerLinkContainer>
        );
    }

    closeScanCardFromFooter = () => this.props.closeScanCard('card upload screen');

    handlePhotoCaptureChange = () => this.chooseScanCardPhoto();

    render() {
        const {
            intl, privacyCheckboxClicked, isPrivacyDialogOpen, isPrivacyPolicyCheckboxChecked,
        } = this.props;

        const shouldRenderInsuranceCardBack = this.shouldRenderInsuranceCardBack();

        const frontCaptureBoxText = intl.formatMessage(LOCALIZED_STRINGS.FrontCaptureBoxText);

        return (
            <div>
                <ScanCardContainer>
                    <Title shouldRenderInsuranceCardBack={shouldRenderInsuranceCardBack}>
                        <FormattedMessage
                            defaultMessage="Take a photo of the front"
                            id="Components.InsurancePicker.ScanCardUpload.UploadFrontHeader"
                        />
                    </Title>

                    <Metrics component="InsurancePickerScanCard">
                        <CustomMetrics>
                            {({ sendMetric }) => (
                                <React.Fragment>
                                    <PhotoCapture
                                        ref={ref => {
                                            this.insuranceCardFront = ref;
                                        }}
                                        data-test="insurance-card-front"
                                        data-uem-id="insurance-card-front"
                                        enabled={isPrivacyPolicyCheckboxChecked}
                                        onChangeCallback={this.capturedFrontInsuranceCard}
                                        onClickScanCard={() => sendMetric({
                                            element: 'FrontCardScan',
                                            initiator: EVENT_INITIATORS.USER,
                                            interactionType: INTERACTION_TYPES.CLICK,
                                        })}
                                        text={frontCaptureBoxText}
                                    />
                                    {this.renderInsuranceCardBack(() => sendMetric({
                                        element: 'BackCardScan',
                                        initiator: EVENT_INITIATORS.USER,
                                        interactionType: INTERACTION_TYPES.CLICK,
                                    }))}
                                </React.Fragment>
                            )}
                        </CustomMetrics>
                    </Metrics>

                    <CustomMetrics>
                        {({ sendMetric }) => (
                            <CheckboxContainer
                                checked={isPrivacyPolicyCheckboxChecked}
                                onChecked={() => {
                                    privacyCheckboxClicked();
                                    sendMetric({
                                        action: 'Insurance Picker Scan - Privacy Checkbox Toggled',
                                        element: 'PrivacyCheckBox',
                                        initiator: EVENT_INITIATORS.USER,
                                        interactionType: INTERACTION_TYPES.CLICK,
                                    });
                                }}
                            >
                                <CheckboxText>
                                    Please read and accept the&nbsp;
                                    <LegalLink
                                        href="https://www.zocdoc.com/about/terms/"
                                        onClick={this.stopPropagationToCheckbox}
                                        target="_blank"
                                    >
                                        Terms of Use
                                    </LegalLink>
                                    &nbsp; and&nbsp;
                                    <LegalLink
                                        href="https://www.zocdoc.com/about/privacypolicy/"
                                        onClick={this.stopPropagationToCheckbox}
                                        target="_blank"
                                    >
                                        Privacy Policy
                                    </LegalLink>
                                    .
                                </CheckboxText>
                            </CheckboxContainer>
                        )}
                    </CustomMetrics>
                </ScanCardContainer>

                {isPrivacyDialogOpen ? this.renderModal() : null}

                {this.renderFooter()}
            </div>
        );
    }

    shouldRenderInsuranceCardBack = () => {
        const { frontCardUploaded, isPrivacyPolicyCheckboxChecked } = this.props;

        return frontCardUploaded === true && isPrivacyPolicyCheckboxChecked === true;
    };

    renderInsuranceCardBack = onClick => {
        const { intl } = this.props;
        const shouldRenderInsuranceCardBack = this.shouldRenderInsuranceCardBack();

        if (shouldRenderInsuranceCardBack === true) {
            const backCaptureBoxText = intl.formatMessage(LOCALIZED_STRINGS.BackCaptureBoxText);
            return (
                <InsuranceCardBack
                    data-test="insurance-card-back"
                    data-uem-id="insurance-card-back"
                >
                    <PhotoCapture
                        ref={ref => {
                            this.insuranceCardBack = ref;
                        }}
                        onChangeCallback={this.capturedBackInsuranceCard}
                        onClickScanCard={onClick}
                        text={backCaptureBoxText}
                    />
                </InsuranceCardBack>
            );
        }
    };

    stopPropagationToCheckbox(e) {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        return false;
    }

    capturedFrontInsuranceCard = () => {
        this.props.capturedFrontInsuranceCard();
    };

    capturedBackInsuranceCard = () => {
        const backImg = this.insuranceCardBack.getImageData(true);
        const frontImg = this.insuranceCardFront.getImageData(true);
        this.props.fetchScanCard(frontImg, backImg);
    };
}

const ReturnToPickerLinkContainer = styled.div`
    bottom: 0;
    position: fixed;
    text-align: center;
    width: 100%;
    background-color: ${grey10};
    margin: auto;
    margin-bottom: 14px;
    border-top: 1px solid ${grey50};
    padding-top: 16px;
    line-height: 15px;
`;

const ReturnToPickerLink = styled(Link)`
    font-family: ${FontFamily.SharpSansSemibold};
`;

const ScanCardContainer = styled.div`
    max-width: 345px;
    margin: auto;
`;

const Title = styled.div`
    font: 18px/24px ${FontFamily.SharpSansSemibold};
    text-align: center;
    margin-top: ${p => (p.shouldRenderInsuranceCardBack ? '10%' : '48px')};
    margin-bottom: 12px;
`;

const CheckboxContainer = styled(Checkbox)`
    margin-top: 15px;
    line-height: 20px;
`;

const CheckboxText = styled.span`
    display: inline-block;
    margin-left: -3px;
`;

const LegalLink = styled(Link)`
    font: 15px/20px ${FontFamily.SharpSansSemibold};
`;

const InsuranceCardBack = styled.div`
    margin-top: 10px;
`;

export const ScanCardUploadView = injectIntl(BaseScanCardUploadView);
